import { useRef, useEffect, useState, useCallback } from 'react';
import './ChargeTracker.scss';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import DatePicker from 'app/shared/DateTimePicker';
import FleetCharge from './components/FleetCharge';
import FleetBattery from './components/FleetBattery';
import CurrentlyCharging from './components/CurrentlyCharging';
import ChargingEvents from './components/ChargingEvents';
import ChargeHistory from './components/ChargeHistory';
import ChargeUtilization from './components/ChargeUtilization';
import { apiPost } from 'services/api.service';
import { orderBy } from 'lodash';
import moment from 'moment';
import { EV_ENDPOINT_FETCH_CHARGET_TRACKER_DASHBOARD } from 'services/APIConstants';

import { updateSessionMeta } from 'utils/HelperFunctions';
import EmptyState from 'app/shared/EmptyState';
import GroupTree from '../../../../shared/GroupTree/GroupTree';
import {
	updateChargeTrackerData,
	updateChargeTrackerDataLive,
	setChargeTrackerGroupTree,
	setShowingDataFor,
	updateChargeDash,
} from 'app/config/Redux/global.reducer';

function ChargeTracker() {
	// Using useSelector for Redux state
	const chargetracker = useSelector((state) => state.chargetracker);
	const chargetrackerLive = useSelector((state) => state.chargetrackerLive);
	const chargeDash = useSelector((state) => state.chargeDash);
	const myGroup = useSelector((state) => state.myGroup);
	const chargeTrackerGroupTree = useSelector((state) => state.chargeTrackerGroupTree);
	const showingDataFor = useSelector((state) => state.showingDataFor);

	const dispatch = useDispatch();
	const location = useLocation();

	const [isLoading, setLoading] = useState(false);
	const [dashboardData, setDashboardData] = useState(null);
	const [currentDate, setCurrentDate] = useState(moment().startOf('day').valueOf());
	const [isLive, setIsLive] = useState(!chargetracker);
	const interval = useRef();
	const [, updateState] = useState();
	const [isError, setIsError] = useState(false);
	const [groups, setGroups] = useState([]);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const [dataForDate, setDataForDate] = useState(moment().startOf('day').valueOf());
	const [dataForGroup, setDataForGroup] = useState([]);
	const [updatedAt, setUpdatedAt] = useState();

	const forceUpdate = useCallback(() => updateState({}), []);
	const liveData = useSelector((state) => state.chargetrackerLive);

	useEffect(() => {
		// console.log('Current live data in state:', liveData);
	}, [liveData]);

	useEffect(() => {
		setGroups(null);
		initPreserveState();
		interval.current = setInterval(fetchChargeTracker, 600000);
		window.group = [];

		if (showingDataFor?.['chargetracker']) {
			// console.log('inside useeefect', showingDataFor);
			setDataForGroup(showingDataFor['chargetracker']);
		} else {
			// console.log('inside useeefect else part', window.userGroups, groups, selectedGroup);

			setDataForGroup([window.userGroups ? window.userGroups[0] : '']);
		}

		return () => {
			if (interval.current) {
				clearInterval(interval.current);
			}
		};
	}, []);

	useEffect(() => {
		if (chargeTrackerGroupTree.length === 0) {
			let group = { ...myGroup };
			if (!group.path) {
				if (Object.keys(group).length > 0) {
					group = group.children[0];
				} else {
					group['path'] = '/0/0/';
					group['ppath'] = '/0/0/';
				}
				window.group = group;
				if (Object.keys(myGroup).length > 1) {
					setGroups(group);
					navigate(group);
				}
			}
		} else {
			window.group = chargeTrackerGroupTree[0];
			setGroups(chargeTrackerGroupTree);
			navigate(chargeTrackerGroupTree[chargeTrackerGroupTree.length - 1]);
		}
	}, [myGroup]);

	useEffect(() => {
		getLastUpdated();
	}, [chargeDash]);

	const getLastUpdated = () => {
		const data = chargeDash?.currentcharging?.history;
		if (data?.length > 0) {
			const last = Math.max(...data.map((charge) => charge.starttime));
			updatePreserveState('lastupdate', last);
			setUpdatedAt(last ? moment(last).format('DD MMM YYYY, h:mm A') : 'Not available');
		}
	};

	useEffect(() => {
		const dashData = isLive
			? chargetrackerLive
				? { ...chargetrackerLive }
				: null
			: chargetracker
			? { ...chargetracker }
			: null;

		dispatch(updateChargeDash(dashData));
		forceUpdate();
	}, [isLive, chargetrackerLive, chargetracker, dispatch]);

	const initPreserveState = () => {
		const pageKey = location.pathname.replaceAll('/', '');
		const metaData = JSON.parse(sessionStorage.getItem(pageKey));

		if (!metaData) {
			if (!chargetracker && !chargetrackerLive) {
				fetchChargeTracker();
			}
			return;
		}

		if (metaData.date) {
			const dateValue = parseInt(metaData.date);
			setCurrentDate(dateValue);

			if (!chargetracker && !chargetrackerLive) {
				fetchChargeTracker(dateValue);
			}

			setIsLive(moment().format('DD-MM') === moment(dateValue).format('DD-MM'));
		}

		if (metaData.live) {
			setIsLive(metaData.live);
		}
	};

	const processBarData = (dataObj) => {
		return Object.entries(dataObj).map(([hour, data], idx) => ({
			hours: moment(new Date().setHours(hour, 0)).format('H:mm'),
			time: data.time,
			count: data.count,
			events: data.count,
			vehicles: data.vehiclelist,
			key: idx,
		}));
	};

	const updatePreserveState = (key, value) => {
		const pageKey = location.pathname.replaceAll('/', '');
		updateSessionMeta(key, value, pageKey);
	};

	const processDashboardData = (data, date) => {
		Object.entries(data).forEach(([key, value]) => {
			if (['meta', 'chargedistribution', 'vehiclefullycharged'].includes(key)) return;

			if (key === 'batterytemp') {
				value.history = orderBy(value.history, ['time'], ['desc']).sort(
					(a, b) => (a.time === null) - (b.time === null)
				);
			} else if (key === 'todaydistance') {
				data.todaydistance.history = orderBy(value.history, ['soc'], ['desc']).sort(
					(a, b) => (a.soc === null) - (b.soc === null)
				);
			} else if (key === 'todaycharging') {
				data.todaycharging = processBarData(value);
			} else if (key !== 'totalvehicles') {
				value.history = orderBy(value.history, ['time'], ['desc']).sort(
					(a, b) => (a.time === null) - (b.time === null)
				);
			}
		});

		// Use the correct action creator based on date
		const actionCreator =
			moment().format('DD-MM') !== moment(date).format('DD-MM')
				? updateChargeTrackerData
				: updateChargeTrackerDataLive;

		dispatch(actionCreator(data));
		setDashboardData(data);
		setLoading(false);
	};

	const fetchChargeTracker = async (date = currentDate, isReload = false) => {
		if (selectedGroup) {
			setDataForGroup(groups);
			if (showingDataFor) {
				const newDataFor = {
					...showingDataFor,
					chargetracker: groups,
				};
				dispatch(setShowingDataFor(newDataFor));
			}
		}

		setLoading(true);
		if (!isReload) {
			const isCurrentDate = moment().format('DD-MM') === moment(date).format('DD-MM');
			setIsLive(isCurrentDate);
			updatePreserveState('live', isCurrentDate);
		}

		try {
			const payload = {
				starttime: date,
				...(selectedGroup && { groupid: selectedGroup.groupid }),
			};

			const response = await apiPost(EV_ENDPOINT_FETCH_CHARGET_TRACKER_DASHBOARD, payload);

			if (response.status === 'CRASHED') {
				setIsError(true);
			} else {
				processDashboardData(response.data, date);
				setDataForDate(date);
			}
		} catch (error) {
			setIsError(true);
		} finally {
			setLoading(false);
		}
	};

	const renderFetchButton = () => {
		// if (moment().format('DD-MM') !== moment(currentDate).format('DD-MM') || !isLive) {
		return (
			<div
				className={'FetchButton ' + (isLoading ? 'disableButton' : '')}
				onClick={() => fetchChargeTracker()}
			>
				OK
			</div>
		);
		// } else {
		// 	return null;
		// }
	};

	// const goLive = () => {
	// 	setCurrentDate(moment().startOf('day').valueOf());
	// 	setIsLive(true);
	// 	updatePreserveState('live', true);
	// 	updatePreserveState('date', moment().startOf('day').valueOf());
	// 	if (props.chargetrackerLive === null) {
	// 		fetchChargeTracker(moment().startOf('day').valueOf());
	// 	}
	// };

	const navigate = (group) => {
		let rootGroupPath = '';
		let path = group.path;
		let pathArray = path.split('/');
		pathArray.splice(0, 1);
		pathArray = pathArray.splice(0, pathArray.length - 1);
		let currentPath = rootGroupPath;
		let nav = [];
		let currentNode = window.group;
		nav.push(currentNode);

		for (let idx in pathArray) {
			currentPath += '/' + pathArray[idx];
			for (let jdx in currentNode.children) {
				if (currentNode.children[jdx].path === currentPath + '/') {
					currentNode = currentNode.children[jdx];
					if (!('children' in currentNode)) currentNode.children = [];
					nav.push(currentNode);
					break;
				}
			}
		}

		setGroups(nav);
		if (nav.length > 1) {
			dispatch(setChargeTrackerGroupTree(nav));
		} else {
			if (myGroup?.children) {
				dispatch(setChargeTrackerGroupTree([myGroup.children[0]]));
			}
		}
		setSelectedGroup(group);
	};

	return (
		<div className="ChargeTracker">
			<div className="ControlPanel" style={{ justifyContent: 'space-between' }}>
				<div
					className="UpControlPanel"
					style={{ display: 'flex', justifyContent: 'space-between' }}
				>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{/* <div className="LiveControl" onClick={!isLive && !isLoading ? goLive : null}>
							<i className="fad fa-circle"></i>
							<span>{!isLive && !isLoading ? 'Go Live' : ' Live'}</span>
						</div> */}
						<small style={{ fontSize: 10, marginLeft: 10 }}>
							<div
								style={{
									display: 'flex',
									position: 'relative',
									marginLeft: '20px',
									height: '30px',
								}}
							>
								<GroupTree
									props={{ groups: groups, selectedGroup: selectedGroup }}
									navigate={navigate}
								/>
							</div>
						</small>
					</div>

					<div className="RightControl">
						<DatePicker
							onChange={(date) => {
								setCurrentDate(date);
								updatePreserveState('date', date);
							}}
							showTime={false}
							value={currentDate}
						/>
						{renderFetchButton()}
						{/* {isLive ? (
							<div
								className={'button ' + (isLoading ? 'disableButton' : '')}
								onClick={() => fetchChargeTracker(moment().startOf('day').valueOf(), true)}
							>
								<i style={{ color: '#E62F4D' }} className="fas fa-redo"></i>
							</div>
						) : null}  */}
					</div>
				</div>
				{!isLoading ? (
					<div className="DownControlPanel">
						<div className="DataInfoLeft">
							{isLive && updatedAt && <div>* Last updated at {updatedAt}</div>}
						</div>

						<div className="DataInfoCenter">
							<span>Showing for &nbsp;</span>
							{dataForGroup.map((group, key) => {
								if (key < dataForGroup.length - 1) {
									return (
										<div style={{ display: 'flex' }}>
											<span>{` ${group.name}`}</span>
											<div className="hni-icon">
												<span className="fa-solid fa-chevron-right"></span>
											</div>
										</div>
									);
								} else {
									// console.log(selectedGroup, myGroup);
									return (
										<span>{` ${
											group && group.name
												? group.name
												: myGroup && myGroup.children && myGroup.children.length
												? myGroup.children[0].name
												: ''
										}`}</span>
									);
								}
							})}
							<span> &nbsp; on {moment(dataForDate).format('DD MMM YYYY')}</span>
						</div>

						<div className="DataInfoRight">
							<span>Vehicles:&nbsp; </span>
							{isLive
								? chargetrackerLive && (
										<span style={{ width: '4rem' }}>{`  ${chargetrackerLive.totalvehicles}`}</span>
								  )
								: chargetracker && (
										<span style={{ width: '4rem' }}>{`  ${chargetracker.totalvehicles}`}</span>
								  )}
						</div>
					</div>
				) : null}
			</div>
			<div className="Widgets">
				{isLoading ? (
					<div className="loader">
						LOADING<span></span>
					</div>
				) : !chargeDash || isError ? (
					<EmptyState
						title={isError ? 'Something went wrong' : 'No Data'}
						description={
							isError
								? "Looks like we couldn't fetch data for given timerange"
								: 'Looks like we have no record for the selected date'
						}
					/>
				) : (
					<>
						<FleetCharge
							data={{ ...chargeDash.csd } || {}}
							heads={chargeDash.meta.csd}
							isLive={isLive}
						/>
						<CurrentlyCharging
							data={{ ...chargeDash.currentcharging } || {}}
							heads={chargeDash.meta.currentcharging}
							isLive={isLive}
						/>
						<FleetBattery
							data={{ ...chargeDash.batterytemp } || {}}
							heads={chargeDash.meta.batterytemp}
							isLive={isLive}
						/>
						<ChargingEvents data={{ ...chargeDash.chargedistribution } || {}} isLive={isLive} />
						<ChargeHistory
							data={{ ...chargeDash.todaycharging } || []}
							heads={chargeDash.meta.todaycharging}
							isLive={isLive}
						/>
						<ChargeUtilization
							data={{ ...chargeDash.todaydistance } || []}
							heads={chargeDash.meta.todaydistance}
							isLive={isLive}
						/>
					</>
				)}
			</div>
		</div>
	);
}

export default ChargeTracker;
